import { WellDetailsStates } from 'business/models/CustomModels';
import { IImageZoomDTO, IWellImageParams, RegionsDTO } from 'business/models/Models';
import WellService from 'business/services/WellService';
import TransformPoints from 'components/plateviewer/well/TransformPoints';

export const exportPoiCsv = async (
  expId: number,
  plateId: number,
  wellNum: number,
  dropNumber: number,
) => {
  const wellService = new WellService();
  const fileResponse = await wellService.generatePoiCsvFile(
    expId,
    wellNum ? true : false,
    plateId,
    wellNum,
    dropNumber,
  );
  const blob = new Blob([fileResponse.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileResponse.fileName;
  link.click();
};

export const changeDrawingIcon = (e, icon: string) => {
  const stage = e.target.getStage();
  if (!stage) {
    return;
  }
  const container = stage.container();
  if (container.style.cursor === icon) {
    return;
  }

  container.style.cursor = icon;
};

export const getMinimumZoomLevel = (
  isIndependentZoom: boolean,
  totalWells: number,
  minZoomLevel: number,
) => {
  if (isIndependentZoom || totalWells === 1) {
    return 1;
  }
  return minZoomLevel;
};

//key: plateId-wellNum-dropNumber-regionName
const regionIdCache: Record<string, number> = {};

/**
 * Build the full region ID cache from all regions.
 */
export function buildRegionIdLookupCache(allRegions: RegionsDTO[]): void {
  if (!allRegions || allRegions.length === 0) {
    return;
  }

  for (const region of allRegions) {
    if (region.drops?.length) {
      for (const drop of region.drops) {
        if (!drop) {
          continue; // Skip if drop is undefined or null
        }
        const key = `${drop.plateId}-${drop.wellNumber}-${drop.dropNumber}-${region.name}`;
        regionIdCache[key] = drop.regionId;
      }
    }
  }
}

/**
 * Look up a region ID from the cache; if not found, fall back to search logic.
 */
export function getRegionIdCached(
  plateID: number,
  wellNum: number,
  dropNumber: number,
  regionName: string,
  allRegions?: RegionsDTO[],
): number | undefined {
  const key = `${plateID}-${wellNum}-${dropNumber}-${regionName}`;
  // Try cache first
  if (key in regionIdCache) {
    return regionIdCache[key];
  }

  if (!allRegions || allRegions.length === 0) {
    return null;
  }
  // Fallback to inline getRegionId logic --
  // if for some reason cached data is not available(highly unlikely)
  const selectedRegion = allRegions.find((r) => r.name === regionName);
  const regionDetail = selectedRegion?.drops?.find(
    (d) =>
      d.plateId === plateID && d.wellNumber === wellNum && d.dropNumber === dropNumber,
  );

  regionIdCache[key] = regionDetail?.regionId;
  return regionDetail?.regionId;
}

export const getWellImageParams = (WellDetails: WellDetailsStates): IWellImageParams => {
  return {
    pid: WellDetails.plateID,
    wellNumber: WellDetails.wellNum,
    batchId: WellDetails.imageBatchId,
    dropNumber: WellDetails.dropNumber,
    cpID: WellDetails.cpID,
    regionID: WellDetails.regionID,
  };
};

export const getImageZoomVisibleArea = (
  stage,
  tpObject: TransformPoints,
  scale,
): IImageZoomDTO => {
  const position = stage.position();
  const stageWidth = stage.width();
  const stageHeight = stage.height();
  if (!tpObject) {
    return {} as IImageZoomDTO;
  }
  const { imageHeight, imageWidth, scalingWidth, scalingHeight, leftMargin, topMargin } =
    tpObject;

  const scaleRatio = imageWidth / scalingWidth;

  const visibleArea = {
    x: Math.max(0, ((-position.x - leftMargin * scale) / scale) * scaleRatio),
    y: Math.max(0, ((-position.y - topMargin * scale) / scale) * scaleRatio),
    width: Math.min(imageWidth, (stageWidth / scale) * scaleRatio),
    height: Math.min(imageHeight, (stageHeight / scale) * scaleRatio),
  };

  return {
    scale,
    ...visibleArea,
    outputWidth: Math.round(Math.min(stageWidth, scalingWidth * scale)),
    outputHeight: Math.round(Math.min(stageHeight, scalingHeight * scale)),
  } as IImageZoomDTO;
};
